import { useState } from "react";
import { Link } from "react-router-dom";
import { ticketsApi } from "../utils/api";

export default function MyTicketsPage() {
  const [ref, setRef]         = useState("");
  const [order, setOrder]     = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const lookup = async () => {
    if (!ref.trim()) return;
    setLoading(true); setError(""); setOrder(null); setTickets([]);
    try {
      const d = await ticketsApi.getOrder(ref.trim().toUpperCase());
      setOrder(d.order);
      setTickets(d.tickets || []);
    } catch(e) { setError("Order not found. Check your order reference and try again."); }
    finally { setLoading(false); }
  };

  const statusColor = {
    confirmed: { bg:"rgba(34,197,94,0.1)", border:"rgba(34,197,94,0.3)", text:"#86efac", label:"✅ Confirmed" },
    pending: { bg:"rgba(245,158,11,0.1)", border:"rgba(245,158,11,0.3)", text:"#fcd34d", label:"⏳ Pending" },
    bank_transfer_pending: { bg:"rgba(245,158,11,0.1)", border:"rgba(245,158,11,0.3)", text:"#fcd34d", label:"⏳ Awaiting Payment" },
    receipt_uploaded: { bg:"rgba(59,130,246,0.1)", border:"rgba(59,130,246,0.3)", text:"#93c5fd", label:"📋 Receipt Under Review" },
    cancelled: { bg:"rgba(239,68,68,0.1)", border:"rgba(239,68,68,0.3)", text:"#fca5a5", label:"❌ Cancelled" },
  };

  return (
    <div className="min-h-screen pt-24 pb-16 px-4" style={{background:"#07000f"}}>
      {/* Background glows */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-10"
          style={{background:"radial-gradient(circle,#9b3fe8,transparent)"}}/>
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full blur-3xl opacity-8"
          style={{background:"radial-gradient(circle,#f59e0b,transparent)"}}/>
      </div>

      <div className="max-w-2xl mx-auto relative z-10">

        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 glass rounded-full px-4 py-2 text-sm text-gray-400 mb-4">
            <span>🎟️</span> Ticket Lookup
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-white mb-3">My Tickets</h1>
          <p className="text-gray-400">Enter your order reference to view and download your tickets</p>
        </div>

        {/* Search box */}
        <div className="rounded-3xl p-6 mb-6" style={{background:"linear-gradient(135deg,rgba(124,34,200,0.12),rgba(155,63,232,0.05))", border:"1px solid rgba(155,63,232,0.25)"}}>
          <label className="text-xs text-gray-400 font-semibold mb-3 block uppercase tracking-wider">Order Reference</label>
          <div className="flex gap-3 mb-3">
            <input type="text" placeholder="TKT-12345" value={ref}
              onChange={e => setRef(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && lookup()}
              className="flex-1 px-5 py-4 rounded-2xl text-white text-base placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-brand-500 font-mono tracking-widest"
              style={{background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.1)"}}/>
            <button onClick={lookup} disabled={loading || !ref.trim()}
              className="px-8 py-4 rounded-2xl text-white font-bold text-base transition-all hover:scale-105 disabled:opacity-50 disabled:scale-100"
              style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)", boxShadow:"0 8px 24px rgba(124,34,200,0.3)"}}>
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"/>
              ) : "Find"}
            </button>
          </div>
          <p className="text-xs text-gray-600">Your order ref was sent to your WhatsApp & email (e.g. TKT-64842)</p>
        </div>

        {/* Error */}
        {error && (
          <div className="rounded-2xl p-5 mb-6 flex items-center gap-3" style={{background:"rgba(239,68,68,0.08)", border:"1px solid rgba(239,68,68,0.2)"}}>
            <span className="text-2xl">😕</span>
            <div>
              <p className="text-red-300 font-medium text-sm">Order not found</p>
              <p className="text-red-400/70 text-xs mt-0.5">Check your order reference and try again</p>
            </div>
          </div>
        )}

        {/* Results */}
        {order && (
          <div>
            {/* Order card */}
            <div className="rounded-3xl overflow-hidden mb-6" style={{background:"linear-gradient(135deg,rgba(124,34,200,0.12),rgba(155,63,232,0.05))", border:"1px solid rgba(155,63,232,0.25)"}}>
              {order.banner_image && (
                <div className="h-32 relative overflow-hidden">
                  <img src={order.banner_image} alt="" className="w-full h-full object-cover"/>
                  <div className="absolute inset-0" style={{background:"linear-gradient(to bottom,transparent,rgba(7,0,15,0.9))"}}/>
                </div>
              )}
              <div className="p-6">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <p className="font-mono text-brand-300 font-bold text-lg">{order.order_ref}</p>
                    <p className="text-white font-bold text-xl mt-1">{order.event_title}</p>
                    <p className="text-gray-400 text-sm mt-1">{order.tier_name} · {order.quantity} ticket{order.quantity>1?"s":""}</p>
                  </div>
                  {(() => {
                    const s = statusColor[order.status] || statusColor.pending;
                    return (
                      <span className="text-xs px-3 py-1.5 rounded-full font-medium flex-shrink-0"
                        style={{background:s.bg, border:`1px solid ${s.border}`, color:s.text}}>
                        {s.label}
                      </span>
                    );
                  })()}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="glass rounded-xl p-3">
                    <p className="text-gray-500 text-xs mb-1">📅 Date</p>
                    <p className="text-white font-medium text-xs">
                      {order.event_date ? new Date(order.event_date).toLocaleDateString('en-GB',{weekday:'short',day:'numeric',month:'long',year:'numeric'}) : ''}
                    </p>
                  </div>
                  <div className="glass rounded-xl p-3">
                    <p className="text-gray-500 text-xs mb-1">📍 Venue</p>
                    <p className="text-white font-medium text-xs">{order.venue_name}</p>
                  </div>
                </div>

                {/* Pending payment notice */}
                {['bank_transfer_pending','receipt_uploaded','pending'].includes(order.status) && (
                  <div className="mt-4 p-3 rounded-xl" style={{background:"rgba(245,158,11,0.08)", border:"1px solid rgba(245,158,11,0.2)"}}>
                    <p className="text-yellow-300 text-xs">⏳ Your ticket will be sent to WhatsApp after payment confirmation (within 2 hours).</p>
                  </div>
                )}
              </div>
            </div>

            {/* Tickets */}
            {tickets.length > 0 && (
              <>
                <p className="text-xs text-gray-500 font-semibold uppercase tracking-wider mb-3">
                  {tickets.length} QR Ticket{tickets.length>1?"s":""}
                </p>
                <div className="space-y-4">
                  {tickets.map((t, i) => (
                    <div key={t.ticket_uid} className="rounded-2xl overflow-hidden" style={{border:"1px solid rgba(155,63,232,0.2)"}}>
                      <div className="p-5" style={{background:"linear-gradient(135deg,rgba(124,34,200,0.1),rgba(7,0,15,0.95))"}}>
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <p className="text-xs text-gray-500 mb-1">Ticket {i+1} of {tickets.length}</p>
                            <p className="text-white font-bold text-xl">{t.attendee_name}</p>
                            <p className="text-brand-300 text-sm mt-0.5">{order.tier_name}</p>
                            {t.checked_in_at && (
                              <p className="text-green-400 text-xs mt-1">✅ Checked in {new Date(t.checked_in_at).toLocaleTimeString()}</p>
                            )}
                          </div>
                          {/* QR Code */}
                          <div className="flex-shrink-0">
                            <div className="bg-white p-2.5 rounded-2xl shadow-xl">
                              <img src={`/uploads/tickets/qr/${t.ticket_uid}.png`} alt="QR Ticket"
                                width="110" height="110" className="block rounded-lg"
                                onError={e => e.target.style.display="none"}/>
                            </div>
                            <p className="text-xs text-center text-gray-600 mt-1.5">Show at entrance</p>
                          </div>
                        </div>
                      </div>

                      {/* Download buttons */}
                      <div className="px-5 py-3 flex items-center justify-between gap-2 border-t border-white/8"
                        style={{background:"rgba(0,0,0,0.3)"}}>
                        <p className="text-xs font-mono text-gray-700 hidden sm:block">{t.ticket_uid?.slice(0,8).toUpperCase()}...</p>
                        <div className="flex gap-2 ml-auto">
                          <a href={`/api/tickets/ticket/${t.ticket_uid}/pdf`}
                            download={`ticket-${t.attendee_name}.pdf`}
                            className="text-xs text-white font-semibold px-4 py-2 rounded-xl transition hover:opacity-90"
                            style={{background:"linear-gradient(135deg,#7c22c8,#9b3fe8)"}}>
                            📄 PDF
                          </a>
                          <a href={`/uploads/tickets/qr/${t.ticket_uid}.png`}
                            download={`ticket-qr-${t.attendee_name}.png`}
                            className="text-xs glass text-gray-300 hover:text-white px-4 py-2 rounded-xl transition">
                            ⬇ QR Image
                          </a>
                          <Link to={`/verify/${t.ticket_uid}`} target="_blank"
                            className="text-xs glass text-gray-300 hover:text-white px-4 py-2 rounded-xl transition">
                            🔍 Verify
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Empty state */}
        {!order && !loading && !error && (
          <div className="rounded-3xl p-12 text-center glass">
            <div className="text-6xl mb-4">🎟️</div>
            <p className="text-white font-semibold text-xl mb-2">Find Your Tickets</p>
            <p className="text-gray-500 text-sm mb-6 max-w-xs mx-auto">Enter your order reference above — it was sent to your WhatsApp and email after booking</p>
            <Link to="/" className="inline-flex items-center gap-2 text-brand-400 hover:text-brand-300 text-sm font-medium transition">
              ← Browse Events
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
